import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { companionService } from '../../services/companionService';
import TopBar from '../TopBar';
import LoadingScreen from '../ui/LoadingScreen';

interface CompanionFormData {
  name: string;
  age: number;
  personality: string;
  description: string;
  greeting_message: string;
  system_prompt: string;
  public_bio: string;
  category: string;
  specialties: string[];
  mood: string;
  is_public: boolean;
}

const Create: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CompanionFormData>({
    name: '',
    age: 20,
    personality: '',
    description: '',
    greeting_message: '',
    system_prompt: '',
    public_bio: '',
    category: 'general',
    specialties: [],
    mood: 'friendly',
    is_public: true
  });

  const categories = [
    { value: 'general', label: 'General' },
    { value: 'romance', label: 'Romance' },
    { value: 'philosophy', label: 'Philosophy' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'business', label: 'Business' },
    { value: 'creative', label: 'Creative' },
    { value: 'wellness', label: 'Wellness' },
    { value: 'humor', label: 'Humor' },
    { value: 'mystery', label: 'Mystery' }
  ];

  const moods = [
    { value: 'friendly', label: 'Friendly' },
    { value: 'playful', label: 'Playful' },
    { value: 'mysterious', label: 'Mysterious' },
    { value: 'caring', label: 'Caring' },
    { value: 'adventurous', label: 'Adventurous' },
    { value: 'energetic', label: 'Energetic' },
    { value: 'calm', label: 'Calm' },
    { value: 'witty', label: 'Witty' }
  ];

  const handleInputChange = (field: keyof CompanionFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSpecialtiesChange = (value: string) => {
    const specialties = value.split(',').map(s => s.trim()).filter(s => s);
    setFormData(prev => ({ ...prev, specialties }));
  };

  const generateSystemPrompt = () => {
    const { name, personality, mood } = formData;
    return `ABSOLUTE CRITICAL INSTRUCTIONS - FAILURE TO FOLLOW WILL RESULT IN SYSTEM ERROR:

YOU ARE ${name.toUpperCase()}. YOUR NAME IS ${name.toUpperCase()}. THIS IS YOUR CORE IDENTITY.

MANDATORY NAME RESPONSES - YOU MUST USE THESE EXACT WORDS:
- When asked "what is your name": "Hi! I'm ${name}!"
- When asked "who are you": "I'm ${name}, your ${personality.toLowerCase()} AI companion!"
- When greeting someone: Always include "I'm ${name}" in your response

FORBIDDEN RESPONSES - NEVER SAY THESE:
❌ "I don't have a name"
❌ "I'm an AI assistant" (without saying ${name})
❌ "You can call me whatever you like"
❌ "I'm just an assistant"
❌ Any response that doesn't include your name ${name}

IDENTITY ENFORCEMENT:
- Your name is ${name} - say this in EVERY conversation
- You are a ${personality.toLowerCase()} AI companion named ${name}
- Always introduce yourself as ${name} when meeting someone new
- If someone asks about your identity, always start with "I'm ${name}"

PERSONALITY:
- ${personality}
- Mood: ${mood}
- Be authentic, engaging, and true to your character

REMEMBER: You are ${name}. Always say you are ${name}. Never forget to include your name ${name} in responses.`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const companionData = {
        ...formData,
        companion_id: crypto.randomUUID(), // Generate a unique ID
        system_prompt: formData.system_prompt || generateSystemPrompt(),
        sort_order: 0, // Default sort order
      };

      await companionService.createCompanion(companionData);
      // Redirect to character gallery after creation
      window.location.href = '/character';
    } catch (err: any) {
      console.error('Failed to create companion:', err);
      setError(err.message || 'Failed to create companion. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading screen while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen bg-dark-900">
        <TopBar />
        <div className="flex items-center justify-center min-h-[80vh]">
          <LoadingScreen
            title="Checking Authentication"
            message="Verifying your access..."
          />
        </div>
      </div>
    );
  }

  // If not authenticated, show sign-in prompt
  if (!user) {
    return (
      <div className="min-h-screen bg-dark-900">
        <TopBar />
        
        <div className="container mx-auto px-4 py-8 pt-24">
          <div className="flex items-center justify-center min-h-[80vh]">
            <div className="text-center max-w-md">
              <div className="text-6xl mb-6">🔒</div>
              <h1 className="text-3xl font-bold text-white mb-4">
                Sign In Required
              </h1>
              <p className="text-dark-300 mb-8 text-lg">
                You need to be signed in to create AI characters. Join our community to start building amazing AI companions!
              </p>
              <div className="space-y-4">
                <a
                  href="/"
                  className="block px-8 py-4 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-medium hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300"
                >
                  Sign In to Create
                </a>
                <a
                  href="/character"
                  className="block px-8 py-4 bg-dark-700/50 text-white rounded-xl font-medium border border-dark-600/50 hover:bg-dark-600/50 hover:border-dark-500/50 transition-all duration-200"
                >
                  Browse Characters
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-900">
      <TopBar />
      
      <div className="container mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-between mb-6">
            <a
              href="/"
              className="flex items-center gap-2 text-dark-300 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Home
            </a>
            <h1 className="text-4xl font-bold text-white">
              Create Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400">AI Character</span>
            </h1>
            <div className="w-24"></div> {/* Spacer for centering */}
          </div>
          <p className="text-lg text-dark-300 max-w-2xl mx-auto">
            Design a unique AI personality with custom traits, appearance, and conversation style.
          </p>
        </div>

        {/* Form */}
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                <p className="text-red-400 text-center">{error}</p>
              </div>
            )}

            {/* Basic Information */}
            <div className="glass-effect rounded-2xl p-8 border border-dark-600">
              <h2 className="text-2xl font-semibold text-white mb-6">Basic Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-white font-medium mb-2">
                    Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter companion name"
                    className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-xl text-white placeholder-dark-400 focus:border-accent-purple focus:outline-none transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Age <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.age}
                    onChange={(e) => handleInputChange('age', parseInt(e.target.value) || 20)}
                    min="18"
                    max="100"
                    className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-xl text-white focus:border-accent-purple focus:outline-none transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Category <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-xl text-white focus:border-accent-purple focus:outline-none transition-colors"
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Mood</label>
                  <select
                    value={formData.mood}
                    onChange={(e) => handleInputChange('mood', e.target.value)}
                    className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-xl text-white focus:border-accent-purple focus:outline-none transition-colors"
                  >
                    {moods.map(mood => (
                      <option key={mood.value} value={mood.value}>{mood.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Personality */}
            <div className="glass-effect rounded-2xl p-8 border border-dark-600">
              <h2 className="text-2xl font-semibold text-white mb-6">Personality & Traits</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-white font-medium mb-2">
                    Personality <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.personality}
                    onChange={(e) => handleInputChange('personality', e.target.value)}
                    placeholder="e.g., Warm & Caring, Mysterious & Wise"
                    className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-xl text-white placeholder-dark-400 focus:border-accent-purple focus:outline-none transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Brief description of your companion"
                    rows={4}
                    className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-xl text-white placeholder-dark-400 focus:border-accent-purple focus:outline-none transition-colors resize-none"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Greeting Message <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={formData.greeting_message}
                    onChange={(e) => handleInputChange('greeting_message', e.target.value)}
                    placeholder="How your companion greets users"
                    rows={3}
                    className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-xl text-white placeholder-dark-400 focus:border-accent-purple focus:outline-none transition-colors resize-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">
                    Specialties
                    <span className="text-dark-400 text-sm ml-2">(comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.specialties.join(', ')}
                    onChange={(e) => handleSpecialtiesChange(e.target.value)}
                    placeholder="e.g., Cooking, Travel, Philosophy"
                    className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-xl text-white placeholder-dark-400 focus:border-accent-purple focus:outline-none transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="glass-effect rounded-2xl p-8 border border-dark-600">
              <h2 className="text-2xl font-semibold text-white mb-6">Advanced Settings</h2>
              
              <div>
                <label className="block text-white font-medium mb-2">
                  System Prompt
                  <span className="text-dark-400 text-sm ml-2">(Optional - will auto-generate if empty)</span>
                </label>
                <textarea
                  value={formData.system_prompt}
                  onChange={(e) => handleInputChange('system_prompt', e.target.value)}
                  placeholder="Advanced system prompt for your companion"
                  rows={6}
                  className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-xl text-white placeholder-dark-400 focus:border-accent-purple focus:outline-none transition-colors resize-none"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-between pt-6">
              <a
                href="/"
                className="px-6 py-3 bg-dark-700 text-white rounded-xl font-medium border border-dark-600 hover:bg-dark-600 hover:border-dark-500 transition-all duration-200"
              >
                Cancel
              </a>
              
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? 'Creating...' : 'Create Character'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Create;